import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Upload, CheckCircle2, XCircle, Loader2, Clock } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { poiQueries } from '../api/queries';
import { useTriggerPoiImport } from '../api/mutations';
import type { WithCallWithAuth } from '../api/queries';
import type { PoiImportStatus } from '@ooh/contracts';

// ─── Status badge ─────────────────────────────────────────────

const STATUS_CONFIG: Record<
  PoiImportStatus['status'],
  { iconKey: string; icon: React.ElementType; className: string }
> = {
  idle: { iconKey: 'idle', icon: Clock, className: 'text-muted-foreground' },
  running: { iconKey: 'running', icon: Loader2, className: 'text-blue-500' },
  completed: { iconKey: 'completed', icon: CheckCircle2, className: 'text-green-600' },
  failed: { iconKey: 'failed', icon: XCircle, className: 'text-destructive' },
};

function useStatusLabel(status: PoiImportStatus['status']): string {
  const { t } = useTranslation('map');
  if (status === 'running') return t('admin.pois.importTrigger.statusRunning');
  if (status === 'completed') return t('admin.pois.importTrigger.statusCompleted');
  if (status === 'failed') return t('admin.pois.importTrigger.statusFailed');
  return t('admin.pois.importTrigger.statusIdle');
}

function StatusBadge({ status }: { status: PoiImportStatus['status'] }) {
  const { icon: Icon, className } = STATUS_CONFIG[status];
  const label = useStatusLabel(status);

  return (
    <span className={`flex items-center gap-1 text-[13px] font-medium ${className}`}>
      <Icon
        className={`h-3.5 w-3.5 ${status === 'running' ? 'animate-spin' : ''}`}
        strokeWidth={1.8}
      />
      {label}
    </span>
  );
}

// ─── Status info section ──────────────────────────────────────

function StatusInfo({
  importStatus,
  isLoading,
  lastRunLabel,
}: {
  importStatus: PoiImportStatus | undefined;
  isLoading: boolean;
  lastRunLabel: string | null;
}) {
  const { t } = useTranslation('map');

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-3">
        {isLoading ? (
          <div className="h-5 w-20 rounded bg-muted animate-pulse" />
        ) : (
          <StatusBadge status={importStatus?.status ?? 'idle'} />
        )}
        {lastRunLabel && !isLoading ? (
          <span className="text-[12px] text-muted-foreground">
            {t('admin.pois.importTrigger.lastRunAt')}: {lastRunLabel}
          </span>
        ) : null}
      </div>
      {importStatus?.status === 'failed' && importStatus.lastError ? (
        <p className="text-[12px] text-destructive mt-1 break-all">
          {t('admin.pois.importTrigger.lastError')}: {importStatus.lastError}
        </p>
      ) : null}
    </div>
  );
}

// ─── Import panel ─────────────────────────────────────────────

interface PoiImportPanelProps {
  auth: WithCallWithAuth;
}

export function PoiImportPanel({ auth }: PoiImportPanelProps) {
  const { t, i18n } = useTranslation('map');

  const {
    data: importStatus,
    isLoading,
    refetch,
  } = useQuery({
    ...poiQueries.importStatus(auth),
    refetchOnWindowFocus: false,
    refetchInterval: (query) => (query.state.data?.status === 'running' ? 10_000 : false),
  });

  const { mutate: triggerImport, isPending } = useTriggerPoiImport(auth);

  const isRunning = importStatus?.status === 'running' || isPending;

  const lastRunLabel = React.useMemo(() => {
    if (!importStatus?.lastRunAt) return null;
    return new Date(importStatus.lastRunAt).toLocaleString(i18n.language, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  }, [importStatus?.lastRunAt, i18n.language]);

  function handleTrigger() {
    triggerImport(undefined, {
      onSuccess: () => {
        void refetch();
      },
    });
  }

  return (
    <div
      className="rounded-xl border border-border bg-card p-5 space-y-3"
      data-testid="poi-import-panel"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h3 className="text-[14px] font-semibold text-foreground flex items-center gap-2">
            <Upload className="h-4 w-4 text-muted-foreground" strokeWidth={1.6} />
            {t('admin.pois.importTrigger.statusLabel')}
          </h3>
          <StatusInfo
            importStatus={importStatus}
            isLoading={isLoading}
            lastRunLabel={lastRunLabel}
          />
        </div>

        <Button
          size="xs"
          onClick={handleTrigger}
          disabled={isRunning}
          data-testid="trigger-poi-import-button"
        >
          {isRunning
            ? t('admin.pois.importTrigger.triggerButtonRunning')
            : t('admin.pois.importTrigger.triggerButton')}
        </Button>
      </div>
    </div>
  );
}
